import React, { useState, useEffect } from "react";
import { GitBranch, Copy, Sparkles, Zap, ChevronDown } from "lucide-react";

const CATEGORY_META = {
  development_process: { label: "Dev Process", color: "#7c3aed", bg: "#ede9fe" },
  code_quality:        { label: "Code Quality", color: "#0369a1", bg: "#e0f2fe" },
  testing:             { label: "Testing",      color: "#065f46", bg: "#d1fae5" },
  ci_cd:               { label: "CI/CD",        color: "#92400e", bg: "#fef3c7" },
  architecture:        { label: "Architecture", color: "#be185d", bg: "#fce7f3" },
  security:            { label: "Security",     color: "#dc2626", bg: "#fee2e2" },
  observability:       { label: "Observability",color: "#1d4ed8", bg: "#dbeafe" },
  collaboration:       { label: "Collaboration",color: "#0f766e", bg: "#ccfbf1" },
  incident_management: { label: "Incidents",    color: "#b45309", bg: "#fef9c3" },
  client_engagement:   { label: "Client",       color: "#7e22ce", bg: "#f3e8ff" },
};

function PracticeCard({ practice, onCopy, isNew }) {
  const [showSource, setShowSource] = useState(false);
  const meta = CATEGORY_META[practice.category] || {
    label: practice.category || "Practice",
    color: "#374151",
    bg: "#f3f4f6",
  };
  const hasInsight = Boolean(practice.llm_response);
  const copyText = hasInsight ? practice.llm_response : practice.content;

  return (
    <div
      className={`relative rounded-lg border p-3 mb-3 transition-all ${
        isNew ? "border-purple-400 shadow-md" : "border-gray-200"
      }`}
      style={{ background: isNew ? "#faf5ff" : "#fff" }}
    >
      {isNew && (
        <span className="absolute -top-2 -right-2 flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-purple-600 text-white shadow">
          <Zap size={9} />
          Live
        </span>
      )}

      {/* Header: category badge + copy */}
      <div className="flex items-center justify-between mb-1.5">
        <span
          className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide"
          style={{ color: meta.color, background: meta.bg }}
        >
          {meta.label}
        </span>
        <button
          type="button"
          onClick={() => onCopy(copyText)}
          className="p-1 text-gray-400 hover:text-gray-700 transition-colors"
          title={hasInsight ? "Copy insight" : "Copy content"}
        >
          <Copy size={12} />
        </button>
      </div>

      {/* Title */}
      <div className="font-semibold text-gray-900 text-sm mb-1.5 leading-snug">
        {practice.title}
      </div>

      {/* LLM Insight — primary display when present */}
      {hasInsight ? (
        <div className="mb-2">
          <div className="flex items-center gap-1 mb-1">
            <Sparkles size={10} className="text-purple-500" />
            <span className="text-[10px] font-semibold text-purple-600 uppercase tracking-wide">AI Insight</span>
          </div>
          <p className="text-gray-800 text-xs leading-relaxed">
            {practice.llm_response}
          </p>
          <button
            type="button"
            onClick={() => setShowSource((s) => !s)}
            className="mt-2 flex items-center gap-1 text-[10px] text-gray-400 hover:text-gray-600 transition-colors"
          >
            <ChevronDown
              size={11}
              className={`transition-transform ${showSource ? "rotate-180" : ""}`}
            />
            {showSource ? "Hide" : "View"} reference
          </button>
          {showSource && (
            <div className="mt-1.5 pl-2 border-l-2 border-gray-200">
              <p className="text-gray-500 text-[11px] leading-relaxed">{practice.content}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="text-gray-700 text-xs leading-relaxed line-clamp-4 mb-2">
          {practice.content}
        </div>
      )}

      {/* Tags */}
      {practice.tags && practice.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {practice.tags.slice(0, 6).map((tag) => (
            <span
              key={tag}
              className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 border border-gray-200"
            >
              {tag}
            </span>
          ))}
          {practice.tags.length > 6 && (
            <span className="text-[10px] text-gray-400">+{practice.tags.length - 6}</span>
          )}
        </div>
      )}

      {practice.score != null && practice.score < 1.0 && (
        <div className="mt-1 text-[10px] text-gray-400">
          Relevance: {Math.round(practice.score * 100)}%
        </div>
      )}
    </div>
  );
}

export default function EngineeringPracticesPanel({
  highlights,
  fontSize,
  onAdjustFont,
  onCopyToClipboard,
}) {
  const [newHighlightId, setNewHighlightId] = useState(null);

  useEffect(() => {
    if (highlights.length === 0) return;
    const latest = highlights[highlights.length - 1];
    setNewHighlightId(latest.id);
    const t = setTimeout(() => setNewHighlightId(null), 6000);
    return () => clearTimeout(t);
  }, [highlights.length]);

  return (
    <div className="flex flex-col min-h-0 h-full">
      <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-200 flex-shrink-0">
        <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
          <GitBranch className="w-4 h-4 text-purple-600" />
          Engineering Practices
          {highlights.length > 0 && (
            <span className="text-xs font-normal text-gray-500">({highlights.length})</span>
          )}
        </h3>
        <div className="flex items-center gap-1 text-gray-500">
          <button
            type="button"
            onClick={() => onAdjustFont("engineering", -1)}
            className="p-1 hover:text-gray-900"
            title="Decrease font size"
          >
            <span className="text-xs font-bold">A-</span>
          </button>
          <button
            type="button"
            onClick={() => onAdjustFont("engineering", 1)}
            className="p-1 hover:text-gray-900"
            title="Increase font size"
          >
            <span className="text-xs font-bold">A+</span>
          </button>
        </div>
      </div>

      <div
        className="flex-1 min-h-0 overflow-y-auto custom-scrollbar pr-1"
        style={{ fontSize: `${fontSize}px` }}
      >
        {highlights.length === 0 ? (
          <div className="text-gray-400 italic py-8 text-center text-xs leading-relaxed">
            Engineering practice cards appear automatically as the conversation touches relevant topics.
          </div>
        ) : (
          [...highlights].reverse().map((p) => (
            <PracticeCard
              key={p.id}
              practice={{ ...p, category: p.category || p.cap_type }}
              onCopy={onCopyToClipboard}
              isNew={p.id === newHighlightId}
            />
          ))
        )}
      </div>
    </div>
  );
}
